import { apiFetch } from "./client";

export interface AzureRegion {
  id: string;
  name: string;
  location: string;
}

export interface AzureVmSize {
  id: string;
  name: string;
  cores: number;
  memory: number;
  costPerMonth: number;
  description: string;
}

export interface AzureDeployment {
  id: string;
  companyId: string;
  agentId: string | null;
  resourceType: string;
  resourceId: string | null;
  resourceGroup: string | null;
  region: string | null;
  vmSize: string | null;
  status: string;
  provisioningState: string | null;
  ipAddress: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface AzureSchedule {
  id: string;
  deploymentId: string;
  action: string;
  cronExpression: string;
  timezone: string;
  enabled: boolean;
  lastRunAt: string | null;
  createdAt: string;
  nextRun?: string | null;
  isActive?: boolean;
}

export interface DiscoveredSubscription {
  id: string;
  name: string;
  tenantId: string;
  state: string;
}

export interface DiscoveredResourceGroup {
  name: string;
  location: string;
  provisioningState: string;
}

export interface DiscoveredKeyVault {
  name: string;
  location: string;
  vaultUri: string;
  resourceGroup: string;
}

export interface PlatformSetupConfig {
  subscriptionId: string;
  tenantId: string;
  resourceGroup: string;
  keyVaultUrl: string;
  defaultRegion: string;
}

export interface SpCredentials {
  spTenantId: string;
  spClientId: string;
  spClientSecret: string;
}

export const azureApi = {
  // Discovery — pass SP credentials via POST body
  discoverConnection: (sp?: SpCredentials) =>
    apiFetch<{ success: boolean; tenantId?: string; error?: string }>(
      "/azure/discover/connection",
      { method: "POST", body: JSON.stringify(sp ?? {}) },
    ),
  discoverSubscriptions: (sp?: SpCredentials) =>
    apiFetch<{ success: boolean; subscriptions: DiscoveredSubscription[] }>(
      "/azure/discover/subscriptions",
      { method: "POST", body: JSON.stringify(sp ?? {}) },
    ),
  discoverResourceGroups: (subscriptionId: string, sp?: SpCredentials) =>
    apiFetch<{ success: boolean; resourceGroups: DiscoveredResourceGroup[] }>(
      "/azure/discover/resource-groups",
      { method: "POST", body: JSON.stringify({ subscriptionId, ...(sp ?? {}) }) },
    ),
  discoverKeyVaults: (subscriptionId: string, resourceGroupName: string, sp?: SpCredentials) =>
    apiFetch<{ success: boolean; keyVaults: DiscoveredKeyVault[] }>(
      "/azure/discover/key-vaults",
      { method: "POST", body: JSON.stringify({ subscriptionId, resourceGroupName, ...(sp ?? {}) }) },
    ),

  // Setup (create resources + save config)
  createResourceGroup: (data: { subscriptionId: string; name: string; location: string } & Partial<SpCredentials>) =>
    apiFetch<{ success: boolean; resourceGroup: DiscoveredResourceGroup }>(
      "/azure/setup/resource-group",
      { method: "POST", body: JSON.stringify(data) },
    ),
  createKeyVault: (data: { subscriptionId: string; resourceGroupName: string; name: string; location: string; tenantId: string } & Partial<SpCredentials>) =>
    apiFetch<{ success: boolean; keyVault: DiscoveredKeyVault }>(
      "/azure/setup/key-vault",
      { method: "POST", body: JSON.stringify(data) },
    ),
  saveSetupConfig: (data: PlatformSetupConfig & { clientId?: string; clientSecret?: string }) =>
    apiFetch<{ success: boolean }>("/azure/setup/save-config", { method: "POST", body: JSON.stringify(data) }),
  getSetupConfig: () =>
    apiFetch<{ configured: boolean; config: PlatformSetupConfig | null }>("/azure/setup/config"),

  // Config
  getConfig: () => apiFetch<{ configured: boolean; hasCredentials: boolean; hasSubscription: boolean }>("/azure/config"),
  testCredentials: (data: { tenantId: string; clientId: string; clientSecret: string }) =>
    apiFetch<{ success: boolean; subscriptions?: Array<{ id: string; name: string }>; error?: string }>(
      "/azure/test-credentials", { method: "POST", body: JSON.stringify(data) },
    ),

  // Reference data
  listRegions: () => apiFetch<{ success: boolean; regions: AzureRegion[] }>("/azure/regions"),
  listVmSizes: () => apiFetch<{ success: boolean; vmSizes: AzureVmSize[] }>("/azure/vm-sizes"),
  listSubscriptions: () => apiFetch<{ success: boolean; subscriptions: Array<{ id: string; name: string }> }>("/azure/subscriptions"),

  // Networking
  listVnets: () => apiFetch<{ success: boolean; vnets: unknown[] }>("/azure/vnets"),
  setupNetworking: (data: Record<string, unknown>) =>
    apiFetch<{ success: boolean; networking: unknown }>("/azure/setup-networking", { method: "POST", body: JSON.stringify(data) }),

  // Tenant Key Vault
  setupTenantVault: (companyId: string, location?: string) =>
    apiFetch<{ success: boolean; alreadyExists: boolean; vaultUrl: string; vaultName: string; resourceGroup?: string; error?: string }>(
      `/azure/setup-tenant-vault?companyId=${companyId}`,
      { method: "POST", body: JSON.stringify({ location: location || "uksouth" }) },
    ),

  // Deployments
  listDeployments: (companyId: string) => apiFetch<AzureDeployment[]>(`/azure/deployments?companyId=${companyId}`),
  getDeployment: (id: string) => apiFetch<AzureDeployment>(`/azure/deployments/${id}`),
  createDeployment: (data: Record<string, unknown>) =>
    apiFetch<AzureDeployment>("/azure/deployments", { method: "POST", body: JSON.stringify(data) }),
  startDeployment: (id: string) =>
    apiFetch<{ success: boolean }>(`/azure/deployments/${id}/start`, { method: "POST" }),
  stopDeployment: (id: string) =>
    apiFetch<{ success: boolean }>(`/azure/deployments/${id}/stop`, { method: "POST" }),
  deleteDeployment: (id: string) =>
    apiFetch<{ success: boolean }>(`/azure/deployments/${id}`, { method: "DELETE" }),
  getDeploymentStatus: (id: string) =>
    apiFetch<{ vmName: string; powerState: string; provisioningState: string }>(`/azure/deployments/${id}/status`),
  getDeploymentCost: (id: string) =>
    apiFetch<{ cost: number; currency: string; isEstimated: boolean }>(`/azure/deployments/${id}/cost`),

  // Schedules
  listSchedules: () => apiFetch<AzureSchedule[]>("/azure/schedules"),
  createSchedule: (data: Record<string, unknown>) =>
    apiFetch<AzureSchedule>("/azure/schedules", { method: "POST", body: JSON.stringify(data) }),
  deleteSchedule: (id: string) =>
    apiFetch<void>(`/azure/schedules/${id}`, { method: "DELETE" }),
};
